import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle, WarningCircle, ArrowSquareOut, Plug, Buildings, ArrowsClockwise, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuickBooksModule() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.get("/quickbooks/health");
      setHealth(r.data);
      if (r.data.connected) {
        try {
          const ci = await api.get("/quickbooks/company-info");
          setCompany(ci.data);
        } catch (e) { /* token peut nécessiter reconnexion */ }
      }
    } catch (e) {
      toast.error("Impossible de charger l'état QuickBooks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("connected") === "1") {
      toast.success("QuickBooks connecté avec succès !");
    }
  }, []);

  async function connect() {
    try {
      const r = await api.get("/quickbooks/connect-url");
      if (r.data.auth_url) window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(`Connexion impossible : ${e.response?.data?.detail || e.message}`);
    }
  }

  async function disconnect() {
    if (!window.confirm("Déconnecter QuickBooks ? Les tokens seront supprimés.")) return;
    setBusy(true);
    try {
      await api.post("/quickbooks/disconnect");
      toast.success("QuickBooks déconnecté");
      setCompany(null);
      await load();
    } catch (e) {
      toast.error("Échec de la déconnexion");
    } finally {
      setBusy(false);
    }
  }

  async function testConnection() {
    setBusy(true);
    try {
      const ci = await api.get("/quickbooks/company-info");
      setCompany(ci.data);
      toast.success(`Connexion OK — ${ci.data.company_name || "entreprise"}`);
    } catch (e) {
      toast.error(`Test échoué : ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function syncPending() {
    setBusy(true);
    try {
      const r = await api.post("/quickbooks/sync-pending-receipts");
      if (r.data.skipped) {
        toast.warning(`Sync ignorée : ${r.data.reason}`);
      } else {
        toast.success(`${r.data.pushed} reçu(s) poussé(s) vers QuickBooks${r.data.errors ? ` · ${r.data.errors} erreur(s)` : ""}`);
      }
    } catch (e) {
      toast.error(`Sync échouée : ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-6 text-zinc-400">Chargement…</div>;

  const configured = health?.configured;
  const connected = health?.connected;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="quickbooks-module">
      <header className="border-b border-zinc-800 pb-4">
        <div className="flex items-center gap-2 text-emerald-400">
          <Buildings size={22} weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold">QuickBooks Online</span>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
            {health?.environment === "production" ? "Production" : "Sandbox (test)"}
          </span>
        </div>
        <h1 className="text-3xl font-bold text-zinc-100 mt-1">Comptabilité QuickBooks</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Connecte ton compte QBO pour pousser tes reçus et dépenses vers ta comptabilité (mode « à vérifier »).
        </p>
      </header>

      <div className="grid gap-3">
        <Status ok={configured} label="Application QuickBooks configurée"
          detail={configured ? "Client ID, Secret et Redirect URI présents" : "Variables QBO_CLIENT_ID / SECRET manquantes dans backend/.env"}
          testid="status-configured" />
        <Status ok={connected} label="Compte QuickBooks connecté"
          detail={connected ? `Entreprise : ${health.company_name || company?.company_name || health.realm_id}` : "Aucune entreprise autorisée pour le moment"}
          testid="status-connected" />
      </div>

      {!connected && configured && (
        <button onClick={connect} data-testid="btn-connect-quickbooks"
          className="w-full px-6 py-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold uppercase tracking-widest transition flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20">
          <Plug size={22} weight="fill" /> Connecter QuickBooks <ArrowSquareOut size={16} />
        </button>
      )}

      {connected && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-emerald-950/40 border border-emerald-800 text-sm text-emerald-200" data-testid="connected-card">
            <div className="flex items-center gap-2 font-bold">
              <CheckCircle size={18} weight="fill" /> Connecté à QuickBooks {health?.environment === "production" ? "(Production)" : "(Sandbox)"}
            </div>
            {company && (
              <div className="mt-2 text-xs text-emerald-300/90 space-y-0.5">
                <div>Entreprise : <strong>{company.company_name || "—"}</strong></div>
                {company.legal_name && <div>Raison sociale : {company.legal_name}</div>}
                {company.country && <div>Pays : {company.country}</div>}
                <div>Realm ID : {health.realm_id}</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={testConnection} disabled={busy} data-testid="btn-test-connection"
              className="px-4 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold flex items-center gap-2 transition disabled:opacity-50">
              <ArrowsClockwise size={16} /> Tester la connexion
            </button>
            <button onClick={disconnect} disabled={busy} data-testid="btn-disconnect-quickbooks"
              className="px-4 py-2.5 rounded-lg bg-red-950/60 hover:bg-red-900/60 text-red-200 border border-red-900 text-sm font-semibold flex items-center gap-2 transition disabled:opacity-50">
              Déconnecter
            </button>
          </div>

          <section className="p-4 rounded-lg bg-zinc-900/60 border border-zinc-800 space-y-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300 flex items-center gap-2">
              <Receipt size={16} /> Synchronisation des reçus
            </h2>
            <p className="text-sm text-zinc-400">
              Pousse tes reçus bookkeeping vers QuickBooks en tant que dépenses (Purchase), avec note « à vérifier »
              pour ton comptable. Compte de paiement et de dépense auto-sélectionnés depuis ton plan comptable QBO.
              <span className="block mt-1 text-xs text-emerald-300/80">⏱️ Sync automatique chaque jour à 23h59 — ou pousse manuellement ci-dessous.</span>
            </p>
            <button onClick={syncPending} disabled={busy} data-testid="btn-sync-pending-receipts"
              className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center gap-2 transition disabled:opacity-50">
              <Receipt size={16} /> Pousser les reçus en attente vers QBO
            </button>
          </section>
        </div>
      )}

      {configured && (
        <section className="space-y-3 p-4 rounded-lg bg-zinc-900/60 border border-zinc-800">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300">📋 Avant de connecter</h2>
          <ol className="text-sm text-zinc-400 list-decimal list-inside space-y-2">
            <li>Va sur <a href="https://developer.intuit.com/app/developer/dashboard" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">developer.intuit.com</a> → ton app <strong className="text-zinc-200">Kaizen Comptable</strong></li>
            <li>Dans <strong>Keys &amp; credentials</strong> ({health?.environment === "production" ? "Production" : "Development"}), ajoute le Redirect URI :{" "}
              <code className="text-xs bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300 break-all">{health?.redirect_uri}</code>
            </li>
            <li>Sauvegarde, reviens ici et clique <strong>« Connecter QuickBooks »</strong>.</li>
            <li className="text-amber-300/80">⚠️ Tu es en mode <strong>{health?.environment === "production" ? "PRODUCTION (vraies données)" : "SANDBOX (données de test)"}</strong>.</li>
          </ol>
        </section>
      )}
    </div>
  );
}

function Status({ ok, label, detail, testid }) {
  return (
    <div data-testid={testid}
      className={`p-3 rounded-lg border flex items-start gap-3 ${ok ? "bg-emerald-950/30 border-emerald-800/60" : "bg-zinc-900/60 border-zinc-800"}`}>
      {ok ? <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> : <WarningCircle size={20} className="text-amber-400 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${ok ? "text-emerald-200" : "text-zinc-200"}`}>{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{detail}</div>
      </div>
    </div>
  );
}
